import { BEYOND_WORDS_SCRIPT } from "@config/beyondWords";
import { registerPlayerEvents } from "@elements/BeyondWordsPlayer/events";
import { BEYOND_WORDS_PROJECT_ID } from "@helper/getEnvVariables";
import { useAudioAnalytics } from "@hooks/useAnalyticsCall/useAudioAnalytics";
import useScript from "@hooks/useScript";
import {
  BeyondWordsPlayerConfig,
  BeyondWordsPlayerInstance,
  BeyondWordsPlayerProps,
} from "@typings/BeyondWords.d";
import { useEffect, useRef } from "react";

export const useBeyondWordsPlayer = ({
  cueId,
  title,
  articleId,
  isInfiniteScrollArticle,
  visibleArticleId,
}: BeyondWordsPlayerProps) => {
  const playerRef = useRef<HTMLDivElement>(null);
  const initializedRef = useRef(false);
  const tmAudioTracking = useAudioAnalytics();
  const playerRefId = useRef(
    `beyondwords-ref-${cueId}-${Math.random().toString(36).substring(2, 11)}`,
  );

  const { isScriptReady } = useScript({
    src: BEYOND_WORDS_SCRIPT,
    id: "beyondwords-player-script",
    checkScriptIsLoaded: () =>
      typeof window !== "undefined" && !!window.BeyondWords,
  });

  useEffect(() => {
    if (!isScriptReady || !playerRef.current || initializedRef.current) return;

    initializedRef.current = true;

    const inlineTarget = document.createElement("div");
    inlineTarget.className = "bw-inline-target";
    playerRef.current.appendChild(inlineTarget);

    let cleanWidgetTarget: HTMLDivElement | null = null;
    const stickyContainer = document.getElementById(
      `tts-sticky-widget-${cueId}`,
    );
    if (stickyContainer) {
      cleanWidgetTarget = document.createElement("div");
      cleanWidgetTarget.className = "bw-sticky-target";
      stickyContainer.appendChild(cleanWidgetTarget);
    }

    const playerProps: BeyondWordsPlayerConfig = {
      target: inlineTarget,
      widgetTarget: cleanWidgetTarget || `#tts-sticky-widget-${cueId}`,
      widgetStyle: "none",
      projectId: BEYOND_WORDS_PROJECT_ID,
      sourceId: cueId,
      playerStyle: "standard",
      clientSideEnabled: false,
      logoIconEnabled: false,
      playlistToggle: "hide",
      downloadFormats: [],
      callToAction: "கேளுங்கள்",
      backgroundColor: "#F7F3F4",
      textColor: "#212529",
      iconColor: "#B10442",
    };

    const beyondWords = window.BeyondWords as {
      Player: new (
        config: BeyondWordsPlayerConfig,
      ) => BeyondWordsPlayerInstance;
    };
    const player = new beyondWords.Player(playerProps);

    registerPlayerEvents(player, tmAudioTracking);

    const handlePauseAllPlayers = (
      event: CustomEvent<{ sourceId: string }>,
    ) => {
      if (event.detail.sourceId !== playerRefId.current) {
        if (typeof player.stop === "function") {
          player.stop();
        } else {
          player.playbackState = "paused";
        }
      }
    };

    window.addEventListener(
      "pauseAllPlayers",
      handlePauseAllPlayers as EventListener,
    );

    window.beyondWordsPlayerInstance = player;
    if (!window.beyondWordsPlayerInstances) {
      window.beyondWordsPlayerInstances = {};
    }
    window.beyondWordsPlayerInstances[cueId] = player;
    window.dispatchEvent(new CustomEvent(`beyondwords-ready-${cueId}`));

    player.addEventListener("PlaybackPlaying", () => {
      window.dispatchEvent(
        new CustomEvent(`beyondwords-visibility-${cueId}`, {
          detail: { visible: true },
        }),
      );
    });

    const handlePlayerStop = () => {
      window.dispatchEvent(
        new CustomEvent(`beyondwords-visibility-${cueId}`, {
          detail: { visible: false },
        }),
      );
    };

    player.addEventListener("PlaybackPaused", handlePlayerStop);
    player.addEventListener("PlaybackEnded", handlePlayerStop);

    const setPlaybackForVisible = (visible: boolean) => {
      try {
        if (visible) {
          player.widgetStyle = "standard";
          if (typeof player.play === "function") {
            player.play();
          } else {
            player.playbackState = "playing";
          }
          window.dispatchEvent(
            new CustomEvent("pauseAllPlayers", {
              detail: { sourceId: playerRefId.current },
            }),
          );
        } else {
          if (player.widgetStyle !== "closed-by-user") {
            player.widgetStyle = "none";
          }
          if (typeof player.stop === "function") {
            player.stop();
          } else {
            player.playbackState = "paused";
          }
        }

        window.dispatchEvent(
          new CustomEvent(`beyondwords-visibility-${cueId}`, {
            detail: { visible },
          }),
        );
      } catch {
        // Ignore player transition errors to keep page stable.
      }
    };

    const handleToggle = (e: Event) => {
      const customEvent = e as CustomEvent<{ visible: boolean }>;
      setPlaybackForVisible(customEvent.detail.visible);
    };

    window.addEventListener(
      `toggle-tts-${cueId}`,
      handleToggle as EventListener,
    );

    const origDestroy = player.destroy.bind(player);
    player.destroy = function (
      this: BeyondWordsPlayerInstance,
      ...args: unknown[]
    ) {
      try {
        if (window.beyondWordsPlayerInstances) {
          window.beyondWordsPlayerInstances[cueId] = undefined;
        }
        window.removeEventListener(
          `toggle-tts-${cueId}`,
          handleToggle as EventListener,
        );
      } catch {
        // ignore teardown errors
      }
      origDestroy(...args);
    };

    return () => {
      player.destroy();
      inlineTarget.remove();
      cleanWidgetTarget?.remove();
      window.removeEventListener(
        "pauseAllPlayers",
        handlePauseAllPlayers as EventListener,
      );
    };
  }, [isScriptReady, cueId, tmAudioTracking]);

  useEffect(() => {
    if (
      !visibleArticleId ||
      !initializedRef.current ||
      visibleArticleId !== cueId ||
      !isInfiniteScrollArticle
    ) {
      return;
    }

    const currentPlayer = window.beyondWordsPlayerInstances?.[cueId];
    if (!currentPlayer) return;

    const otherPlayingId = Object.keys(
      window.beyondWordsPlayerInstances || {},
    ).find(
      (id) =>
        id !== cueId &&
        window.beyondWordsPlayerInstances?.[id]?.playbackState === "playing",
    );

    if (otherPlayingId && window.beyondWordsPlayerInstances) {
      const otherPlayer = window.beyondWordsPlayerInstances[otherPlayingId];
      if (otherPlayer) {
        if (typeof otherPlayer.stop === "function") {
          otherPlayer.stop();
        } else {
          otherPlayer.playbackState = "paused";
        }
        otherPlayer.widgetStyle = "none";
        window.dispatchEvent(
          new CustomEvent(`beyondwords-visibility-${otherPlayingId}`, {
            detail: { visible: false },
          }),
        );
      }

      if (typeof currentPlayer.play === "function") {
        currentPlayer.play();
      } else {
        currentPlayer.playbackState = "playing";
      }
      currentPlayer.widgetStyle = "standard";

      // Ensure sticky widget visibility updates even if PlaybackPlaying
      // is delayed/missed during programmatic handover.
      window.dispatchEvent(
        new CustomEvent(`beyondwords-visibility-${cueId}`, {
          detail: { visible: true },
        }),
      );

      window.dispatchEvent(
        new CustomEvent("pauseAllPlayers", {
          detail: { sourceId: playerRefId.current },
        }),
      );
    }
  }, [visibleArticleId, cueId, isInfiniteScrollArticle, title, articleId]);

  return playerRef;
};
